import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Volume2, VolumeX } from "lucide-react";
import { OPEN_SUPPORT_WIDGET_EVENT } from "@/lib/supportWidgetEvents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Friendly generic "agent" avatar -- a flat-design person silhouette in a
// colored badge. Drawn inline as SVG rather than a photo asset: no image
// file to host/license, always renders instantly, and scales crisply at
// both the header size and the small per-message size.
const AgentAvatar = ({ size = 32 }) => (
  <div
    className="rounded-full bg-[#A67C52] flex items-center justify-center flex-shrink-0 ring-2 ring-black/20"
    style={{ width: size, height: size }}
  >
    <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} fill="none">
      <circle cx="12" cy="8.5" r="4" fill="black" fillOpacity="0.85" />
      <path d="M4 20.5c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="black" fillOpacity="0.85" />
    </svg>
  </div>
);

// Short two-tone chime via the Web Audio API -- no audio file to host, and
// it plays reliably since it's always triggered from inside the send()
// call stack (itself a direct result of a user click/Enter keypress), which
// satisfies browsers' autoplay-requires-a-user-gesture rule.
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.start(now);
    osc.stop(now + 0.35);
    osc.onended = () => ctx.close();
  } catch { /* Web Audio unavailable/blocked -- fine to skip silently */ }
};

export const SupportWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your ResumePilot360 support agent. Ask me anything about building, styles, AI tools, plans, or downloads." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Remote-open trigger (account dropdown's "Help & Support" item) -- see
  // lib/supportWidgetEvents.js for why this is a plain window event rather
  // than lifted state: this widget is a sibling of the routed app tree
  // (App.js), not a descendant of AppShell/AccountMenu.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_SUPPORT_WIDGET_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SUPPORT_WIDGET_EVENT, onOpen);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const r = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history: next }),
      });
      const data = await r.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "Sorry, please try again." }]);
      if (soundOn) playChime();
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now — please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  // 2026-09-29: pages with the phone bottom action bar (MobileActionBar)
  // get the bubble right above that bar; the editor/workspaces (no bar, own
  // bottom controls) keep the raised bottom-52 offset explained below.
  const { pathname } = useLocation();
  const WORKSPACES = ["/editor", "/style-studio/", "/job-match-analyzer/", "/resume-compiler/", "/ats-optimizer/workspace", "/ats-analyzer/"];
  const phoneBottom = WORKSPACES.some((p) => pathname.startsWith(p)) ? "bottom-52" : "bottom-[92px]";

  return (
    <>
      {/* Mobile offset raised from bottom-20 (80px) to bottom-52 (208px) --
          the landing page's Phase 1 hero redesign moved its primary CTA
          button lower in the card than the old layout had, and at
          first-paint (no scroll yet) this fixed-position button could land
          on top of it. Tested empirically against the hero's real,
          roughly-constant-from-top button position across common mobile
          viewport HEIGHTS (the collision risk scales with height, not
          width, since content reflows the same vertically regardless of a
          ~360-430px-wide viewport) -- 375x667 up through 414x896 all clear
          with this offset; a shorter bottom-28/36 still overlapped on
          taller phones (iPhone 11/XR-class, ~896px). Desktop (`lg:`)
          position is untouched. Same component, same behavior, only the
          resting offset moved. */}
      <button
        data-testid="support-toggle"
        onClick={() => setOpen((o) => !o)}
        className={`fixed ${phoneBottom} lg:bottom-5 right-5 z-[80] bg-[#A67C52] text-[#052E22] w-14 h-14 rounded-full shadow-2xl shadow-[#A67C52]/40 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform`}
        title="Support"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="support-panel"
          className="fixed bottom-72 lg:bottom-24 right-5 z-[80] w-[92vw] max-w-sm h-[70vh] max-h-[520px] bg-[#0F211B] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-black/40 border-b border-white/10 px-4 py-3 flex items-center gap-2.5">
            <AgentAvatar size={28} />
            <div className="flex-1 min-w-0">
              <span className="block text-sm font-black uppercase tracking-widest text-white truncate">Support Agent</span>
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" /> Online
              </span>
            </div>
            <button
              data-testid="support-sound-toggle"
              onClick={() => setSoundOn((s) => !s)}
              title={soundOn ? "Mute reply sound" : "Unmute reply sound"}
              className="text-neutral-500 hover:text-white transition-colors p-1"
            >
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <AgentAvatar size={22} />}
                <div
                  className={`max-w-[80%] text-[12px] leading-relaxed px-3 py-2 rounded-lg ${
                    m.role === "user" ? "bg-[#A67C52] text-[#052E22]" : "bg-neutral-900 text-neutral-200 border border-white/10"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-end gap-2 justify-start">
                <AgentAvatar size={22} />
                <div className="bg-neutral-900 border border-white/10 px-3 py-2 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-[#D4A574]" /></div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              data-testid="support-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…"
              className="flex-1 bg-[#16332A] border border-white/10 focus:border-[#D4A574] text-white text-[12px] px-3 py-2 rounded-md outline-none"
            />
            <button data-testid="support-send" onClick={send} disabled={loading} className="bg-[#A67C52] text-[#052E22] px-3 rounded-md flex items-center justify-center disabled:opacity-60">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
